import { createFaq, deleteFaq, fetchFaqs, updateFaq } from '@/apis/faq'
import type { FaqPayload, FaqsListFilters, TeamFaq, TeamFaqApiItem } from '@/types/faq'

const normalizeFaqItems = (items: TeamFaqApiItem[] = []): TeamFaq[] =>
  items.map((item) => ({
    id: item.id,
    question: item.question,
    answer: item.answer,
    visibilityType: item.visibility_type || 'public',
    sortOrder: item.sort_order ?? 0,
    topics: item.topics || [],
    teamTagIds: item.team_tag_ids || [],
    teamTagNames: item.team_tag_names || [],
    teamUnitIds: item.team_unit_ids || [],
    teamUnitNames: item.team_unit_names || [],
    freelancerIds: item.freelancer_ids || [],
    freelancerNames: item.freelancer_names || [],
    updatedAt: item.updated_at || null,
    createdAt: item.created_at || null,
    raw: item,
  }))

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { data?: { message?: string; data?: { message?: string } } }
      message?: string
    }
    if (err.response?.data?.message) return err.response.data.message
    if (err.response?.data?.data?.message) return err.response.data.data.message
    if (err.message) return err.message
  }
  return fallback
}

export const faqService = {
  getFaqs: async (filters: FaqsListFilters = {}) => {
    try {
      const response = await fetchFaqs({
        search: filters.search || undefined,
        team_id: filters.team_id || undefined,
        visibility_type: filters.visibility_type || undefined,
        topics:
          filters.topics && filters.topics.length > 0
            ? filters.topics.join(',')
            : undefined,
        team_tag_ids:
          filters.team_tag_ids && filters.team_tag_ids.length > 0
            ? filters.team_tag_ids.join(',')
            : undefined,
        team_unit_ids:
          filters.team_unit_ids && filters.team_unit_ids.length > 0
            ? filters.team_unit_ids.join(',')
            : undefined,
        freelancer_ids:
          filters.freelancer_ids && filters.freelancer_ids.length > 0
            ? filters.freelancer_ids.join(',')
            : undefined,
        page: filters.page || undefined,
        pageSize: filters.pageSize || undefined,
      })

      const payload = response?.data?.data || {}
      const list = normalizeFaqItems(payload.items || [])

      return {
        success: true as const,
        data: {
          list,
          total: payload.total ?? list.length,
          pageSize: payload.per_page ?? filters.pageSize,
          availableTopics: Array.isArray(payload.available_topics)
            ? payload.available_topics
            : [],
        },
      }
    } catch (error) {
      console.error('Failed to fetch FAQs:', error)
      return {
        success: false as const,
        error,
      }
    }
  },

  createFaq: async (payload: FaqPayload) => {
    try {
      const response = await createFaq(payload)
      return { success: true as const, data: response?.data }
    } catch (error) {
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Failed to create FAQ'),
        errorDetails: error,
      }
    }
  },

  updateFaq: async (faqId: string, payload: FaqPayload) => {
    if (!faqId) return { success: false as const, error: 'FAQ ID is required' }
    try {
      const response = await updateFaq(faqId, payload)
      return { success: true as const, data: response?.data }
    } catch (error) {
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Failed to update FAQ'),
        errorDetails: error,
      }
    }
  },

  deleteFaq: async (faqId: string) => {
    if (!faqId) return { success: false as const }
    try {
      await deleteFaq(faqId)
      return { success: true as const }
    } catch (error) {
      return {
        success: false as const,
        error,
      }
    }
  },
}

export default faqService
